<script lang="ts">
export type TextTruncateTextElement = 'a' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'p' | 'span';

const MEASUREMENT_HOST_ID = 'apx-text-truncate-measurement-host';

/** The computed text styling that affects measured width, resolved once per element + class set. */
type FontMetrics = {
  font: string;
  letterSpacing: string;
  textTransform: string;
  /** Horizontal padding + border the text element carries from its classes; subtracted from the available width. */
  horizontalInset: number;
};

/**
 * Text is measured with a shared canvas 2D context instead of by laying out DOM nodes.
 * measureText() is pure computation, so a page with thousands of truncating cells costs
 * no synchronous layouts. The computed font for a given element + class set is read once
 * from a probe element in the hidden host and cached; responsive classes (lg:text-lg) can
 * change that result, so the cache is cleared whenever the viewport resizes.
 *
 * Canvas models font, letter-spacing, text-transform and the element's own horizontal padding and
 * border. It does not model font-feature-settings (e.g. tabular-nums) or word-spacing; keep
 * textClasses to font-affecting utilities.
 */
let measurementContext: CanvasRenderingContext2D | null | undefined;
const fontMetricsCache = new Map<string, FontMetrics>();
let measurementUsers = 0;
const clearFontMetrics = () => fontMetricsCache.clear();

const retainMeasurement = () => {
  if (measurementUsers === 0) {
    window.addEventListener('resize', clearFontMetrics);
  }
  measurementUsers += 1;
};

const releaseMeasurement = () => {
  measurementUsers -= 1;
  if (measurementUsers === 0) {
    window.removeEventListener('resize', clearFontMetrics);
    fontMetricsCache.clear();
    measurementContext = undefined;
    document.getElementById(MEASUREMENT_HOST_ID)?.remove();
  }
};

const getMeasurementContext = () => {
  if (measurementContext === undefined) {
    measurementContext = document.createElement('canvas').getContext('2d');
  }

  return measurementContext;
};

/**
 * Off-screen, hidden host for the font probe. A regular document node rather than a
 * shadow root: document stylesheets don't match elements inside shadow trees, so classes
 * added there (Tailwind utilities) would have no effect on the computed style.
 */
const getMeasurementHost = () => {
  const existing = document.getElementById(MEASUREMENT_HOST_ID);
  if (existing) {
    return existing;
  }

  const host = document.createElement('div');
  host.id = MEASUREMENT_HOST_ID;
  host.style.position = 'absolute';
  host.style.top = '-9999px';
  host.style.left = '-9999px';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);

  return host;
};

const resolveFontMetrics = (
  element: TextTruncateTextElement,
  classes: string[],
  onInvalidClass: (className: string) => void,
): FontMetrics => {
  const cacheKey = `${element}|${classes.join(' ')}`;
  const cached = fontMetricsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const host = getMeasurementHost();
  const probe = document.createElement(element);
  classes.forEach(className => {
    try {
      probe.classList.add(className.trim());
    } catch {
      onInvalidClass(className);
    }
  });
  host.appendChild(probe);

  const style = getComputedStyle(probe);
  const px = (value: string) => parseFloat(value) || 0;
  const metrics: FontMetrics = {
    font: `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`.trim(),
    letterSpacing: style.letterSpacing === 'normal' ? '0px' : style.letterSpacing,
    textTransform: style.textTransform,
    horizontalInset: px(style.paddingLeft) + px(style.paddingRight) + px(style.borderLeftWidth) + px(style.borderRightWidth),
  };

  host.removeChild(probe);
  fontMetricsCache.set(cacheKey, metrics);

  return metrics;
};

</script>
<script setup lang="ts">
import {
  computed,
  h,
  onMounted,
  onUnmounted,
  ref,
  useAttrs,
  watch,
} from 'vue';
import { useUtilities } from '@apparatix/composables';

type Props = {
  text: string,
  /**
   * Array of classes the truncated text will use. Each class should be an individual array item
   * @default undefined
   */
  textClasses?: string[],
  /**
   * 'word' will truncate the text so that a full word is always visible at the end
   * 'exact' will fit exactly as many characters as there is horizontal space for
   * @default 'exact'
   */
  breakStrategy?: 'word' | 'exact';
  /**
   * Whether or not to show the … character at the end of truncated text. This is only shown if the text is truncated
   * @default true
   */
  showEllipsis?: boolean,
  /**
   * html element to use to contain the text
   * @default 'span'
   */
  element?: TextTruncateTextElement,
  /**
   * href for when 'element' is 'a'
   * @default undefined
   */
  href?: string,
  /**
   * Always show at least this many characters, even if it will cause overflow. This prop is unused when breakStrategy is 'word'
   * @default 3
   */
  minCharacters?: number,
};

type Emits = {
  (e: 'truncated', didTruncate: boolean): void,
};

/** Canvas does not apply CSS text-transform, so mirror the cases that change width. */
const applyTextTransform = (text: string, textTransform: string) => {
  switch (textTransform) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    default:
      return text;
  }
};

/** Largest n in [low, high] for which fits(n) holds. fits is monotone: more text is never narrower. */
const largestFitting = (low: number, high: number, fits: (n: number) => boolean) => {
  let lower = low;
  let upper = high;

  while (lower < upper) {
    const mid = Math.ceil((lower + upper) / 2);
    if (fits(mid)) {
      lower = mid;
    } else {
      upper = mid - 1;
    }
  }

  return lower;
};

const props = withDefaults(defineProps<Props>(), {
  textClasses: undefined,
  breakStrategy: 'exact',
  showEllipsis: true,
  element: 'span',
  href: undefined,
  minCharacters: 3,
});

const emit = defineEmits<Emits>();

const attrs = useAttrs();

const { developerWarning } = useUtilities();

const container = ref<HTMLDivElement>();
const observer = ref<ResizeObserver>();
const truncatedText = ref<string>();

/**
 * Callers can style the text either via the textClasses prop or via a plain class attribute
 * (which Vue applies to the root div, whose font styles the text inherits). The font probe
 * must carry both, or text measured at default styling gets under- or over-truncated
 * relative to what actually renders.
 */
const measurementClasses = computed<string[]>(() => {
  const bound = attrs.class;
  const boundClasses = typeof bound === 'string' ? bound.split(/\s+/) : [];

  return [...boundClasses, ...(props.textClasses ?? [])].filter(Boolean);
});

const warnInvalidClass = (className: string) => developerWarning(
  true,
  `Invalid class passed to TextTruncate: '${className}'.\nThis is most likely due to spaces in the class (eg ['text-base font-bold'] instead of ['text-base', 'font-bold']), or passing a falsy value`,
  'console:warn',
);

const truncate = (containerWidth: number) => {
  const context = getMeasurementContext();
  if (!context) {
    // Without canvas 2D there is nothing to measure with; showing the text beats guessing.
    truncatedText.value = props.text;
    emit('truncated', false);
    return;
  }

  const metrics = resolveFontMetrics(props.element, measurementClasses.value, warnInvalidClass);
  context.font = metrics.font;
  if ('letterSpacing' in context) {
    (context as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = metrics.letterSpacing;
  }

  const measure = (text: string) => context.measureText(applyTextTransform(text, metrics.textTransform)).width;
  const suffix = props.showEllipsis ? '…' : '';
  const { text } = props;
  const availableWidth = containerWidth - metrics.horizontalInset;

  // Every measurement starts from a clean slate so widening a container restores the full text.
  let result: string | undefined;

  if (measure(text) > availableWidth) {
    if (props.breakStrategy === 'exact') {
      const cut = (length: number) => `${text.substring(0, length).trimEnd()}${suffix}`;
      const fitting = largestFitting(0, text.length - 1, length => measure(cut(length)) <= availableWidth);
      const kept = Math.max(fitting, Math.min(props.minCharacters, text.length - 1));

      let truncated = text.substring(0, kept).trimEnd();
      // A period right before the ellipsis reads as four dots
      if (props.showEllipsis && truncated.endsWith('.')) {
        truncated = truncated.slice(0, -1).trimEnd();
      }

      result = `${truncated}${suffix}`;
    } else {
      const words = text.split(/\s/);
      const cut = (count: number) => `${words.slice(0, count).join(' ')}${suffix}`;
      const fitting = largestFitting(0, words.length - 1, count => measure(cut(count)) <= availableWidth);

      // Always show at least one word, even if it overflows
      result = cut(Math.max(fitting, 1));
    }
  }

  truncatedText.value = result ?? text;
  emit('truncated', result !== undefined);
};

/** Re-measure at the current container width. Before mount the ResizeObserver does the first measurement. */
const remeasure = () => {
  if (!container.value) {
    return;
  }

  truncate(container.value.getBoundingClientRect().width);
};

watch(() => props.text, newText => {
  if (!newText) {
    truncatedText.value = undefined;
    return;
  }

  remeasure();
});

/**
 * A font change (e.g. a density switch from text-sm to text-xs) changes how much fits without
 * changing the container width, so the ResizeObserver stays silent. Watch the joined class
 * string, not the array: callers often pass a fresh array literal on every render.
 */
watch(() => measurementClasses.value.join(' '), remeasure);

const resizeCallback: ResizeObserverCallback = entries => {
  const [entry] = entries;
  if (!entry?.target?.checkVisibility()) {
    return;
  }

  truncate(entry.contentRect.width);
};

const renderTextNode = () => {
  const textProps: any = {
    class: props.textClasses,
  };

  if (props.element === 'a' && props.href) {
    textProps.href = props.href;
    textProps.target = '_blank';
  }

  return () => h(props.element, { ...textProps }, truncatedText.value);
};

onMounted(() => {
  retainMeasurement();
  observer.value = new ResizeObserver(resizeCallback);
  observer.value.observe(container.value);
});

onUnmounted(() => {
  observer.value?.disconnect();
  releaseMeasurement();
});
</script>
<template>
  <div ref="container" class="w-full overflow-x-hidden overflow-y-clip">
    <component :is="renderTextNode()" />
  </div>
</template>
